import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { Input, Button, Hint, ErrorSummary } from 'govuk-react-jsx';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  getPmcLinkedEnterpriseNumber,
  setPmcLinkedEnterpriseNumber,
  setPMCData,
} from '../../features/pmc/pmcJourneySlice';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import { checkValidLinkedEnterprise } from '../../regex/regex';
import { getCompanyData } from '../../firebase';
import useTitle from '../../hooks/useTitle';

const PmcLinkedEnterpriseNumber = () => {
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  const linkedEnterpriseNumberRedux = useSelector(getPmcLinkedEnterpriseNumber);
  const [errorMsg, setErrorMsg] = useState(null);
  const [errorList, setErrorList] = useState([]);
  const dispatch = useDispatch();
  const history = useHistory();

  useTitle(
    'pmcClientReg.PmcLinkedEnterpriseNumber.s12',
    'Enter linked enterprise registration number - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const handleCompanyRegistration = (event) => {
    dispatch(setPmcLinkedEnterpriseNumber(event.target.value));
  };

  const errorInvalidFormat = copyProvider.getCopy(
    'pmcClientReg.PmcLinkedEnterpriseNumber.s1',
    'Enter a valid Company Registration number that consists of 8 characters and can contain letters and numbers'
  );

  const errorNotValidAPI = copyProvider.getCopy(
    'pmcClientReg.PmcLinkedEnterpriseNumber.s2',
    'Entered Company Registration number not recognised'
  );

  const errorAlreadyExists = copyProvider.getCopy(
    'pmcClientReg.PmcLinkedEnterpriseNumber.s0',
    'This number is already in use with another account'
  );

  const showErrorInvalidFormat = {
    children: errorInvalidFormat,
  };

  const showErrorNotValidAPI = {
    children: errorNotValidAPI,
  };

  const showErrorAlreadyExists = {
    children: errorAlreadyExists,
  };

  const handleBtnClick = () => {
    if (checkValidLinkedEnterprise.test(linkedEnterpriseNumberRedux)) {
      setErrorMsg(null);
      getCompanyData({ companyRegNumber: linkedEnterpriseNumberRedux })
        .then((response) => {
          const { data } = response;

          dispatch(setPMCData(data));

          history.push('/pmcClientReg/confirm-business-details');
        })
        .catch((err) => {
          console.log(err.message);
          if (
            err.message === 'The company registration number already exists.'
          ) {
            setErrorMsg(showErrorAlreadyExists);
            setErrorList([
              { ...showErrorAlreadyExists, href: '#companyRegistrationNumber' },
            ]);
          } else {
            setErrorMsg(showErrorNotValidAPI);
            setErrorList([
              { ...showErrorNotValidAPI, href: '#companyRegistrationNumber' },
            ]);
          }
        });
    } else if (!checkValidLinkedEnterprise.test(linkedEnterpriseNumberRedux)) {
      setErrorMsg(showErrorInvalidFormat);
      setErrorList([
        { ...showErrorInvalidFormat, href: '#companyRegistrationNumber' },
      ]);
    }
  };

  const pageModel = (
    <>
      {errorList?.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <div className="govuk-form-group">
        <h1 className="govuk-heading-xl">
          {copyProvider.getCopy(
            'pmcClientReg.PmcLinkedEnterpriseNumber.s3',
            'Enter the company registration number of the enterprise that owns or controls your company'
          )}
        </h1>
        <p className="govuk-body">
          {copyProvider.getCopy(
            'pmcClientReg.PmcLinkedEnterpriseNumber.s4',
            'We will use this to confirm the details of your linked enterprise company'
          )}
        </p>

        <p className="govuk-body">
          {copyProvider.getCopy(
            'pmcClientReg.PmcLinkedEnterpriseNumber.s10',
            'This is the best number to use for a fast registration.'
          )}
        </p>

        <div className="govuk-form-group">
          <div className="govuk-body">
            <Input
              errorMessage={errorMsg}
              hint={{
                children: copyProvider.getCopy(
                  'pmcClientReg.PmcLinkedEnterpriseNumber.s6',
                  'Companies House provides this number when a company first registers.'
                ),
              }}
              label={{
                children: copyProvider.getCopy(
                  'pmcClientReg.PmcLinkedEnterpriseNumber.s5',
                  'Company registration number'
                ),
              }}
              id="companyRegistrationNumber"
              className="govuk-input govuk-!-width-two-thirds"
              name="companyRegistration"
              type="text"
              value={linkedEnterpriseNumberRedux}
              onChange={(event) => handleCompanyRegistration(event)}
            />
          </div>
        </div>
      </div>
      <div className="govuk-body govuk-!-font-size-19 govuk-!-padding-bottom-6">
        <Link className="govuk-link " to="/pmcClientReg/company-VAT-number">
          {copyProvider.getCopy(
            'pmcClientReg.PmcLinkedEnterpriseNumber.s7',
            'My client does not have a linked enterprise company registration number'
          )}
        </Link>
        <Hint className="govuk-!-padding-top-4">
          {copyProvider.getCopy(
            'pmcClientReg.PmcLinkedEnterpriseNumber.s11',
            "You can use the VAT registration number of the enterprise that owns or controls your client's company. Ask your client for this information."
          )}
        </Hint>
      </div>

      <Button className="govuk-button" onClick={handleBtnClick}>
        {copyProvider.getCopy(
          'pmcClientReg.PmcLinkedEnterpriseNumber.s8',
          'Continue'
        )}
      </Button>
    </>
  );

  return pageModel;
};

export default PmcLinkedEnterpriseNumber;
